// Field Slices folder builder.
//
// Layout per plane: one pane binding row, whose label cell is hijacked
// to host `[checkbox] [plane-tag]` and whose value cell hosts our own
// RangeControl (mountRangeControl hides the native slider, the hijack rewrites
// the label cell — the two cells are independent). The checkbox toggles plane
// visibility; click on the plane tag toggles the same checkbox for a larger
// hit target.
//
// External state changes (palette shortcut, dataset reload) dispatch
// UI_PANE_REFRESH; the cached row refs re-sync — checkbox from `visKey`, and
// the RangeControl value from `posKey` via `ctrl.set` (which fires no
// callback, so no dispatch loop).

import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { ParamsState } from '@store/schema';
import type { TPaneLike, TFolderLike, TBindingLike, Refs } from '../types';
import { buildDomainStrip, renderDomainStrip, syncDomainSelected, type DomainStripRefs } from './domainStrip';
import { rangeFormat } from '../numberFormat';
import { bindControl, bindControls } from '../bindControl';
import { mountRangeControl, useCustomSlider } from '../rangeAdapter';
import type { RangeControlHandle } from '../../controls/rangeControl';

interface SlicesCtx {
    params: Record<string, unknown>;
    grid: { xMin: number; xMax: number; yMin: number; yMax: number; zMin: number; zMax: number; resolutionX: number; resolutionY: number; resolutionZ: number };
    developerMode?: boolean;
    applySliceDisplaySettings?: () => void;
    rebuildSlicesForCurrentGrid?: () => void;
}

type SliceAxis = 'XY' | 'ZY' | 'ZX';
type VisKey = 'showFieldSlice' | 'showFieldSliceZY' | 'showFieldSliceZX';
type PosKey = 'sliceZ' | 'sliceZY' | 'sliceZX';

interface RowSpec {
    axis: SliceAxis;
    /** Bare axis letter — the normal axis the slider scrubs. Panel header
     *  "Field Slices" supplies the cut-perpendicular-to-axis context. */
    axisLabel: 'X' | 'Y' | 'Z';
    /** Same letter, lower-case, for the underlying coordinate vocabulary. */
    normalAxis: 'x' | 'y' | 'z';
    visKey: VisKey;
    posKey: PosKey;
}

const ROW_SPECS: ReadonlyArray<RowSpec> = [
    { axis: 'XY', axisLabel: 'Z', normalAxis: 'z', visKey: 'showFieldSlice',   posKey: 'sliceZ'  },
    { axis: 'ZY', axisLabel: 'X', normalAxis: 'x', visKey: 'showFieldSliceZY', posKey: 'sliceZY' },
    { axis: 'ZX', axisLabel: 'Y', normalAxis: 'y', visKey: 'showFieldSliceZX', posKey: 'sliceZX' },
];


/** Replace a binding's `.mz-lblv_l` text with a pane-styled checkbox
 *  + plane label. The slider on the right (`.mz-lblv_v`) is untouched.
 *  Returns the checkbox for caller-side state sync, or null on a soft
 *  failure (binding stays usable with its default text label).
 *
 *  The checkbox reuses the `mz-ckbv_l`/`mz-ckbv_i`/`mz-ckbv_w` classes so it
 *  inherits the theme variables (`--in-bg`, `--in-bg-h`, `--in-fg`, `--bld-br`)
 *  for free — the same DOM the dev-mode boolean toggles emit (e.g. the
 *  "Pixelated" toggle in the same Slices folder), so one rule set covers both. */
function hijackLabelCell(binding: TBindingLike, spec: RowSpec): HTMLInputElement | null {
    const rowEl = binding.element;
    if (!rowEl) return null;
    const labelCell = rowEl.querySelector<HTMLElement>('.mz-lblv_l');
    if (!labelCell) return null;
    rowEl.classList.add('slice-binding');
    rowEl.dataset['axis'] = spec.axis;

    while (labelCell.firstChild) labelCell.removeChild(labelCell.firstChild);
    labelCell.classList.add('slice-binding__label');

    // Checkbox DOM, by-the-book:
    //   <label.mz-ckbv_l>
    //     <input.mz-ckbv_i type="checkbox" />
    //     <div.mz-ckbv_w><svg><path/></svg></div>
    //   </label>
    // CSS rule `.mz-ckbv_i:checked+.mz-ckbv_w svg { opacity: 1 }` toggles
    // the checkmark via the adjacent-sibling selector — sibling order
    // matters (input MUST precede the box div).
    const checkLabel = document.createElement('label');
    checkLabel.className = 'mz-ckbv_l slice-binding__check-wrap';
    checkLabel.setAttribute('aria-label', `Show ${spec.axisLabel} slice`);

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'mz-ckbv_i';

    const checkBox = document.createElement('div');
    checkBox.className = 'mz-ckbv_w';
    // SVG checkmark path — the same `M2 8l4 4l8 -8` the shared checkbox
    // primitive draws, so every checkbox glyph matches pixel-for-pixel.
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('d', 'M2 8l4 4l8 -8');
    svg.appendChild(path);
    checkBox.appendChild(svg);

    checkLabel.append(checkbox, checkBox);

    const tag = document.createElement('span');
    tag.className = 'slice-binding__tag';
    tag.textContent = spec.axisLabel;
    // Clicking the tag toggles the checkbox — broader hit target than the
    // 20px square box. Stop propagation so it doesn't hit ancestors.
    tag.addEventListener('click', (e) => {
        e.stopPropagation();
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    });

    labelCell.append(checkLabel, tag);

    checkbox.addEventListener('change', () => {
        // Update the binding's `data-enabled` immediately so the row's
        // dimmed/enabled CSS state flips on user input — the slice
        // visibility intent doesn't dispatch UI_PANE_REFRESH, so we can't
        // wait for the refresh listener to do this for us.
        rowEl.dataset['enabled'] = checkbox.checked ? 'true' : 'false';
        store.dispatch(INTENTS.SET_SLICE_VISIBILITY, { axis: spec.axis, visible: checkbox.checked });
    });

    return checkbox;
}

interface RowRefs {
    spec: RowSpec;
    binding: TBindingLike;
    checkbox: HTMLInputElement | null;
    ctrl: RangeControlHandle | null;
}

function syncRowVisibility(row: RowRefs, params: Record<string, unknown>): void {
    const visible = !!params[row.spec.visKey];
    if (row.checkbox) row.checkbox.checked = visible;
    if (row.binding.element) {
        row.binding.element.dataset['enabled'] = visible ? 'true' : 'false';
    }
    // Reflect the live position onto the custom control (set() is callback-free,
    // so a refresh triggered by SET_SLICE_POSITION can't loop back into dispatch).
    if (row.ctrl) {
        const pos = Number(params[row.spec.posKey]);
        if (Number.isFinite(pos)) row.ctrl.set(pos);
    }
}

interface SliceCustomRefs {
    rows: RowRefs[];
    offRefresh: () => void;
    domain: DomainStripRefs;
}

// Cached on the folder so the UI_PANE_REFRESH listener survives full
// rebuilds (the folder element is stable across `buildSlicesSection`
// calls; only pane children get disposed and recreated).
const CUSTOM_REFS_BY_FOLDER = new WeakMap<TFolderLike, SliceCustomRefs>();

export function buildSlicesSection(paneArg: unknown, refs: Refs, ctx: SlicesCtx): Refs {
    const pane = paneArg as TPaneLike;
    const { params, grid, developerMode } = ctx;
    const { xMin, xMax, yMin, yMax, zMin, zMax, resolutionX, resolutionY, resolutionZ } = grid;
    let folder = refs['slicesFolderRef'] as TFolderLike | undefined;
    if (!folder) {
        folder = pane.addFolder({ title: 'Field Slices', expanded: true });
        if (folder.element) folder.element.dataset['section'] = 'slices';
        refs['slicesFolderRef'] = folder;
    } else {
        try {
            const children = Array.isArray(folder.children) ? folder.children.slice() : [];
            children.forEach((child) => { try { (child as TFolderLike).dispose?.(); } catch { /* swallow */ } });
        } catch { /* swallow */ }
    }
    // Stale UI_PANE_REFRESH listener belongs to disposed binding rows; drop
    // it so the new ones own the refresh fan-out. (The folder element is
    // stable across rebuilds, but the binding rows are not.) Preserve the
    // domain strip — its skeleton lives in the folder body across rebuilds.
    const existingRefs = CUSTOM_REFS_BY_FOLDER.get(folder);
    if (existingRefs) {
        try { existingRefs.offRefresh(); } catch { /* swallow */ }
    }
    // Legacy ref slots kept null for back-compat with sections.ts / pane.ts
    // (they were stored but never read).
    refs['sliceZInputRef'] = null;
    refs['sliceZYInputRef'] = null;
    refs['sliceZXInputRef'] = null;

    // Domain strip — tab-strip UI replacing the prior dropdown. "Full" tile
    // is always first; one tile per registered selection follows. The
    // section registry fans out a 'slices' rebuild on selection
    // add/delete/relabel, so we just re-render tiles from current state.
    // The container is pinned to the BOTTOM of the folder body at the end
    // of this function via `appendChild` (DOM move semantics) — its
    // separator-coloured background visually anchors the pane footer,
    // mirroring the empty top margin and obviating the prior top divider.
    let domain = existingRefs?.domain;
    // Defensive: drop any orphan `.domain-strip` elements from the folder
    // body that aren't the one we're tracking. The WeakMap entry is keyed
    // on the folder, but if folder identity ever changes (HMR, manual
    // refs reset), we'd otherwise accumulate duplicate strips — each with
    // its own stale `aria-selected` state, which the user sees as
    // "multiple tiles selected."
    const folderBodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (folderBodyEl) {
        folderBodyEl.querySelectorAll<HTMLElement>('.domain-strip').forEach((orphan) => {
            if (orphan !== domain?.container) orphan.remove();
        });
    }
    if (!domain) {
        domain = buildDomainStrip('slices');
    } else {
        renderDomainStrip(domain.stripEl, 'slices');
    }

    // Three slice rows — we mount our RangeControl in the value cell and
    // hijack the label cell to host the checkbox + plane tag.
    const stepForAxis = (a: 'x' | 'y' | 'z'): number =>
        (a === 'z' ? resolutionZ : a === 'x' ? resolutionX : resolutionY) || 0.1;
    const rangeForAxis = (a: 'x' | 'y' | 'z'): { min: number; max: number } =>
        a === 'z' ? { min: zMin, max: zMax }
      : a === 'x' ? { min: xMin, max: xMax }
                  : { min: yMin, max: yMax };

    const rows: RowRefs[] = ROW_SPECS.map((spec) => {
        const range = rangeForAxis(spec.normalAxis);
        const step = stepForAxis(spec.normalAxis);
        // The `label` we pass here is replaced by `hijackLabelCell` below;
        // we still give the row a non-empty string so its label cell gets
        // rendered (the `.mz-lblv_l` we then rewrite). The cell has a
        // fixed width via the row's flex layout, so the checkbox + tag
        // sit in a predictable slot.
        const binding = folder.addBinding(params, spec.posKey, {
            min: range.min, max: range.max, step, label: spec.axisLabel, format: rangeFormat(range.min, range.max),
        });
        const send = (v: number): void => {
            store.dispatch(INTENTS.SET_SLICE_POSITION, { axis: spec.axis, value: Math.round(v * 100) / 100 });
        };
        const checkbox = hijackLabelCell(binding, spec);
        // Own the value cell; fall back to the native slider only if the
        // expected row DOM isn't there (or `?nativeslider`).
        const ctrl = useCustomSlider
            ? mountRangeControl(binding, {
                min: range.min, max: range.max, step,
                value: Number(params[spec.posKey]),
                format: rangeFormat(range.min, range.max),
                onInput: (v) => send(v as number),
            })
            : null;
        if (!ctrl) binding.on('change', (ev) => send(ev.value as number));
        const row: RowRefs = { spec, binding, checkbox, ctrl };
        syncRowVisibility(row, params);
        return row;
    });

    // Sync state on UI_PANE_REFRESH so palette shortcuts, keyboard 'S',
    // dataset reloads, and SELECTION_SET_DOMAIN all reflect on the UI.
    // The position re-syncs via `ctrl.set` (callback-free) in syncRowVisibility.
    const offRefresh = store.onIntent(INTENTS.UI_PANE_REFRESH, () => {
        const live = store.get('params') as ParamsState as unknown as Record<string, unknown>;
        rows.forEach((row) => syncRowVisibility(row, live));
        syncDomainSelected('slices');
    });
    CUSTOM_REFS_BY_FOLDER.set(folder, { rows, offRefresh, domain });

    folder.addBlade?.({ view: 'separator' });

    bindControl(folder, params, {
        key: 'sliceOpacity', label: 'Opacity', min: 0, max: 1, step: 0.01,
        onChange: (v) => store.dispatch(INTENTS.SET_SLICE_OPACITY, { value: v as number }),
    });

    if (developerMode) {
        bindControl(folder, params, {
            key: 'sliceResolution', label: 'Slice Resolution', min: 64, max: 1024, step: 32,
            onChange: () => {
                try { if (pane && pane.refresh) pane.refresh(); } catch { /* swallow */ }
                try { if (typeof ctx.rebuildSlicesForCurrentGrid === 'function') ctx.rebuildSlicesForCurrentGrid(); } catch { /* swallow */ }
            },
        });
        bindControl(folder, params, {
            key: 'slicePixelated', label: 'Pixelated',
            onChange: (v) => {
                store.dispatch(INTENTS.SET_SLICE_PIXELATED, { value: !!v });
                try { if (pane && pane.refresh) pane.refresh(); } catch { /* swallow */ }
            },
        });

        // Material controls all share one apply-on-change — express them as data.
        const apply = (): void => { try { if (typeof ctx.applySliceDisplaySettings === 'function') ctx.applySliceDisplaySettings(); } catch { /* swallow */ } };
        bindControls(folder, params, [
            { key: 'sliceMaterial',      label: 'Material', options: { Standard: 'standard', Lambert: 'lambert' }, onChange: apply },
            { key: 'sliceDepthWrite',    label: 'Depth Write',   onChange: apply },
            { key: 'sliceDepthTest',     label: 'Depth Test',    onChange: apply },
            { key: 'sliceDoubleSide',    label: 'Double Sided',  onChange: apply },
            { key: 'sliceAlphaTest',     label: 'Alpha Test',    min: 0.0, max: 0.2, step: 0.005, onChange: apply },
            { key: 'sliceMetalness',     label: 'Metalness',     min: 0.0, max: 1.0, step: 0.01,  onChange: apply },
            { key: 'sliceRoughness',     label: 'Roughness',     min: 0.0, max: 1.0, step: 0.01,  onChange: apply },
            { key: 'sliceRenderOrder',   label: 'Render Order',  min: -10, max: 10,  step: 1,     onChange: apply },
            { key: 'sliceCastShadow',    label: 'Cast Shadow',   onChange: apply },
            { key: 'sliceReceiveShadow', label: 'Receive Shadow', onChange: apply },
        ]);
    }

    // Pin the domain strip to the bottom of the folder body. `appendChild`
    // is a move-or-insert: on rebuilds the strip lives in the body already,
    // here it gets moved past the freshly-mounted pane children.
    const bodyEl = folder.element?.querySelector<HTMLElement>('.mz-fldv_c');
    if (bodyEl) bodyEl.appendChild(domain.container);

    if (pane && pane.refresh) pane.refresh();
    return refs;
}
